import { store } from '@/app/store';
import { analytics } from '@/services/Analytics';
import { portraitGeneration } from '@/services/PortraitGeneration';
import { sentryService } from '@/services/telemetry/sentry';
import { track, warnTelemetryFailure } from '@/shared/utils/track-telemetry';

interface PortraitTelemetryContext {
  errorPortraits: number;
  preloadedPortraits: number;
  selectedPortraits: number;
  totalPortraits: number;
}

interface UsePortraitTelemetryOptions {
  getPortraitContext: () => PortraitTelemetryContext;
  getSelectedPortraitCount: () => number;
  getTotalPortraitCount: () => number;
}

interface StepEntry {
  existingPortraits: number;
  facesSelected: number;
}

type ReadStepEntry = () => StepEntry;

interface OrderSummary {
  groupCount: number;
  individualCount: number;
  totalSum: string;
}

type ReadOrderSummary = () => OrderSummary;

interface RetryPortrait {
  error?: unknown;
  faceId?: unknown;
  isRetrying?: boolean;
  retryCount: number;
  styleId?: unknown;
  uuid: string;
}

type RetryBlockReason = 'already_retrying' | 'max_retries_reached';

export interface PortraitRetryTelemetryContext {
  faceId?: unknown;
  portraitUuid: string;
  previousError: unknown;
  retryCount: number;
}

interface InitialGenerationSpanOptions<T> {
  action: () => Promise<T>;
  facesCount: number;
}

interface RetrySpanOptions {
  action: () => Promise<void>;
  portraitUuid: string;
  retryCount: number;
}

export interface PortraitTelemetry {
  runRetrySpan: (options: RetrySpanOptions) => void;
  startGeneration: () => void;
  trackAllPortraitsGenerated: () => void;
  trackConfirmationOpened: (readOrder: ReadOrderSummary) => void;
  trackEngagement: () => void;
  trackGoPhotoConfirmationSkipped: () => void;
  trackMissingFacesRedirect: () => void;
  trackOrderConfirmed: (readOrder: ReadOrderSummary) => void;
  trackPortraitGenerated: () => void;
  trackRetryAttempt: (portrait: RetryPortrait) => PortraitRetryTelemetryContext;
  trackRetryBlocked: (
    portrait: RetryPortrait,
    reason: RetryBlockReason
  ) => void;
  trackStepEntered: (readEntry: ReadStepEntry) => void;
  trackStepLeft: () => void;
  updatePortraitsContext: () => void;
  withInitialGenerationSpan: <T>(
    options: InitialGenerationSpanOptions<T>
  ) => Promise<T>;
}

function recordAnalyticsTiming(metric: string): void {
  (analytics.trackTimeTo as (metricName: string) => void)(metric);
}

function objectMessage(error: unknown): unknown {
  if (error && typeof error === 'object' && 'message' in error) {
    return error.message;
  }
  return undefined;
}

function retryPreviousError(error: unknown): unknown {
  return objectMessage(error) || error || 'Unknown error';
}

function retryCaptureError(error: unknown): Error {
  return error instanceof Error
    ? error
    : new Error(String(objectMessage(error) || error));
}

export function readPortraitSessionContext(
  selectedCount: number
): PortraitTelemetryContext {
  return {
    totalPortraits: store.portraits?.length || 0,
    preloadedPortraits:
      portraitGeneration.query({ preloaded: true })?.length || 0,
    errorPortraits: portraitGeneration.query({ error: true })?.length || 0,
    selectedPortraits: selectedCount,
  };
}

export function usePortraitTelemetry({
  getPortraitContext,
  getSelectedPortraitCount,
  getTotalPortraitCount,
}: UsePortraitTelemetryOptions): PortraitTelemetry {
  let generationStartedAt: number | null = null;
  let generatedCount = 0;
  let stepStartedAt: number | null = null;

  function getOutstandingErrorCount(): number {
    return getPortraitContext().errorPortraits;
  }

  function trackMissingFacesRedirect(): void {
    track('[Portraits] Sentry call failed:', () => {
      sentryService.addBreadcrumb(
        'navigation',
        'Redirecting to home from portraits (no faces selected)',
        {},
        'warning'
      );
    });
  }

  function trackStepEntered(readEntry: ReadStepEntry): void {
    stepStartedAt = Date.now();
    track('[Portraits] Sentry context error:', () => {
      sentryService.setCurrentStep('portraits');
      sentryService.addBreadcrumb(
        'navigation',
        'Entered individual portraits selection',
        { ...readEntry() }
      );
    });
  }

  function startGeneration(): void {
    generationStartedAt = Date.now();
    generatedCount = 0;
  }

  function updatePortraitsContext(): void {
    track('[Portraits] Context update error:', () => {
      const context = getPortraitContext();
      sentryService.updateSessionContext({
        portraits_total: context.totalPortraits,
        portraits_preloaded: context.preloadedPortraits,
        portraits_errors: context.errorPortraits,
        portraits_selected: context.selectedPortraits,
      });
    });
  }

  function trackPortraitGenerated(): void {
    recordAnalyticsTiming(analytics.METRICS.time_portraits_generated_some);
    generatedCount++;
    track('[Portraits] Metric tracking error:', () => {
      sentryService.trackMetric(
        'portraits.generated_count',
        generatedCount,
        'none',
        {
          type: 'individual',
          session_step: 'portraits',
        }
      );
    });
    updatePortraitsContext();
  }

  function trackAllPortraitsGenerated(): void {
    recordAnalyticsTiming(analytics.METRICS.time_portraits_generated_all);
    const totalTime = Date.now() - (generationStartedAt ?? 0);
    track('[Portraits] Generation completion tracking error:', () => {
      const errorCount = getOutstandingErrorCount();
      sentryService.trackMetric(
        'portraits.generation_time_ms',
        totalTime,
        'millisecond',
        {
          type: 'individual',
          count: generatedCount,
          errors: errorCount,
        }
      );
      sentryService.addBreadcrumb(
        'portrait.generation',
        'All portraits generated and preloaded',
        {
          totalCount: generatedCount,
          errorCount,
          durationMs: totalTime,
        }
      );
    });
  }

  function withInitialGenerationSpan<T>({
    action,
    facesCount,
  }: InitialGenerationSpanOptions<T>): Promise<T> {
    return sentryService.withSpan(
      'portrait.generation.initial',
      'portrait.generation',
      action,
      {
        'portraits.faces_count': facesCount,
        'portraits.type': 'individual',
      }
    );
  }

  function trackStepLeft(): void {
    track('[Portraits] Unmount breadcrumb error:', () => {
      const errorCount = getOutstandingErrorCount();
      sentryService.addBreadcrumb(
        'navigation',
        'Leaving individual portraits selection',
        {
          selectedCount: getSelectedPortraitCount(),
          totalGenerated: generatedCount,
          totalErrors: errorCount,
        }
      );
    });
  }

  function trackRetryBlocked(
    portrait: RetryPortrait,
    reason: RetryBlockReason
  ): void {
    track('[Portraits] Breadcrumb error:', () => {
      sentryService.addBreadcrumb(
        'portrait.retry',
        'Retry blocked',
        {
          reason,
          retryCount: portrait.retryCount,
          // Full portrait UUIDs are operational join keys for generation,
          // print, and storage incident correlation.
          portraitUuid: portrait.uuid,
        },
        'warning'
      );
    });
  }

  function trackRetryAttempt(
    portrait: RetryPortrait
  ): PortraitRetryTelemetryContext {
    const retryContext = {
      portraitUuid: portrait.uuid,
      retryCount: portrait.retryCount + 1,
      previousError: retryPreviousError(portrait.error),
      faceId: portrait.faceId,
    };

    track('[Portraits] Retry tracking error:', () => {
      sentryService.addBreadcrumb(
        'portrait.retry',
        'Retrying portrait generation',
        retryContext
      );
      sentryService.incrementCounter('portraits.retry_attempts', 1, {
        type: 'individual',
        retry_number: retryContext.retryCount,
      });
    });

    if (portrait.error) {
      track('[Portraits] Exception capture error:', () => {
        sentryService.captureException(retryCaptureError(portrait.error), {
          tags: {
            component: 'Portraits',
            operation: 'portrait_retry',
            error_type: 'portrait_generation_retry',
            retry_count: retryContext.retryCount,
          },
          extra: {
            user_action: 'button_click',
            portraits_count: getTotalPortraitCount(),
            selected_count: getSelectedPortraitCount(),
            time_on_step_ms: stepStartedAt ? Date.now() - stepStartedAt : null,
            portraitUuid: portrait.uuid,
            faceId: portrait.faceId,
            styleId: portrait.styleId,
            retryContext,
          },
          level: retryContext.retryCount >= 2 ? 'warning' : 'info',
        });
      });
    }

    return retryContext;
  }

  function runRetrySpan({
    action,
    portraitUuid,
    retryCount,
  }: RetrySpanOptions): void {
    void sentryService
      .withSpan('portrait.generation.retry', 'portrait.generation', action, {
        'portraits.retry_count': retryCount,
        'portraits.portrait_uuid': portraitUuid,
      })
      .catch((error) =>
        warnTelemetryFailure('[Portraits] Retry span error:', error)
      );
  }

  function trackEngagement(): void {
    recordAnalyticsTiming(analytics.METRICS.time_portraits_engaged);
  }

  function trackGoPhotoConfirmationSkipped(): void {
    track('[Portraits] Breadcrumb error:', () => {
      sentryService.addBreadcrumb(
        'portrait.confirmation',
        'Skipping confirmation (GoPhoto mode)',
        { selectedCount: getSelectedPortraitCount() }
      );
    });
  }

  function trackConfirmationOpened(readOrder: ReadOrderSummary): void {
    track('[Portraits] Breadcrumb error:', () => {
      sentryService.addBreadcrumb(
        'portrait.confirmation',
        'Opened order confirmation popup',
        { ...readOrder() }
      );
    });
  }

  function trackOrderConfirmed(readOrder: ReadOrderSummary): void {
    track('[Portraits] Confirmation tracking error:', () => {
      const summary = readOrder();
      const errorCount = getOutstandingErrorCount();
      sentryService.addBreadcrumb(
        'portrait.confirmation',
        'Order confirmed, proceeding to payment',
        {
          ...summary,
          totalPortraitsGenerated: generatedCount,
          generationErrors: errorCount,
        }
      );
      sentryService.incrementCounter('portraits.orders_confirmed', 1, {
        has_group: summary.groupCount > 0 ? 'yes' : 'no',
        has_individual: summary.individualCount > 0 ? 'yes' : 'no',
      });
    });
  }

  return {
    runRetrySpan,
    startGeneration,
    trackAllPortraitsGenerated,
    trackConfirmationOpened,
    trackEngagement,
    trackGoPhotoConfirmationSkipped,
    trackMissingFacesRedirect,
    trackOrderConfirmed,
    trackPortraitGenerated,
    trackRetryAttempt,
    trackRetryBlocked,
    trackStepEntered,
    trackStepLeft,
    updatePortraitsContext,
    withInitialGenerationSpan,
  };
}
